// Per-user navigation pins (nav-redesign PR 6).
//
// Pure array helpers (add/remove/toggle/isPinned) plus the hook that
// persists them. The helpers are unit-tested in tests/web/nav-pins.test.ts.
//
// Storage: pins follow the ACCOUNT, not the browser (2026-08-15). They
// were localStorage-only through v1.x — the original comment here even
// said "cross-device sync would move this to a server preference store
// later." That later is now: pinning your seven most-used screens and
// finding a bare sidebar on the next machine is precisely the thing
// Grant asked to have fixed across the OS. Descriptor lives in
// web/src/settings/user-settings-registry.ts; existing browsers seed the
// account on first load.

import { MAX_NAV_PINS, NAV_PINS, type PinnedNavItem } from "../settings/user-settings-registry";
import { useUserSetting } from "../settings/user-settings-context";
import type { IconName } from "./sidebar-data";

export interface PinnedItem {
  label: string;
  to: string;
  icon: IconName;
}

// The storage key moved to the registry descriptor (NAV_PINS), which is
// now the only thing that touches localStorage — and only to adopt what
// an existing browser already had.
export const MAX_PINS = MAX_NAV_PINS;

export function isPinned(pins: PinnedItem[], to: string): boolean {
  return pins.some((p) => p.to === to);
}

// Add a pin (dedupe by `to`). At the cap, drop the OLDEST so a pin click
// always succeeds (keeps the most-recent MAX_PINS).
export function addPin(pins: PinnedItem[], item: PinnedItem): PinnedItem[] {
  if (isPinned(pins, item.to)) return pins;
  const next = [...pins, { label: item.label, to: item.to, icon: item.icon }];
  return next.length > MAX_PINS ? next.slice(next.length - MAX_PINS) : next;
}

export function removePin(pins: PinnedItem[], to: string): PinnedItem[] {
  return pins.filter((p) => p.to !== to);
}

export function togglePin(pins: PinnedItem[], item: PinnedItem): PinnedItem[] {
  return isPinned(pins, item.to) ? removePin(pins, item.to) : addPin(pins, item);
}

export interface UseNavPinsResult {
  pins: PinnedItem[];
  /** Pin if absent, unpin if present. */
  toggle: (item: PinnedItem) => void;
}

export function useNavPins(): UseNavPinsResult {
  const [stored, setStored] = useUserSetting(NAV_PINS);

  // The registry types `icon` as a plain string (it has no imports, so it
  // can't know IconName). Narrowing happens here, at the one boundary
  // that owns the union — the stored value was written from an IconName
  // in the first place.
  const pins = stored as PinnedItem[];

  const toggle = (item: PinnedItem): void => {
    setStored(togglePin(pins, item) as PinnedNavItem[]);
  };

  return { pins, toggle };
}
